import {
  DesktopOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  MobileOutlined,
  NotificationOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Divider } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { MobileDownloadPopover } from '../../../../../common/components/MobileDownloadPopover/MobileDownloadPopover';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { useDesktopNotifications } from '../../../../../common/hooks/use-desktop-notifications/use-desktop-notifications';
import { SettingsSectionHeader } from '../../common/SettingsSectionHeader/SettingsSectionHeader';
import { NotificationSettings } from '../NotificationSettings/NotificationSettings';
import styles from './Notifications.module.css';

export const Notifications = () => {
  const companyId = useSelector(selectActiveCompanyId);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const modalActions = useModalActions();
  const { updateUserAttributes } = useUsersCRUDActions();

  const {
    id: userId,
    userAttributes: { desktopNotifications },
  } = useSelector((state: StoreState) => state.session.session);

  const areDesktopConversationsFollupsOn =
    desktopNotifications?.needsFollowUp ?? false;
  const areDesktopWidgetTriggeredOn =
    desktopNotifications?.widgetTriggered ?? false;

  const {
    openDesktopNotificationsModal,
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  } = useDesktopNotifications();

  useEffect(() => {
    if (oneSignalPlayerId === null) return;
    const desktopDevices = desktopNotifications?.desktopDevices || [];
    const thisDeviceSaved = desktopDevices.includes(oneSignalPlayerId);
    if (!thisDeviceSaved) {
      updateUserAttributes(companyId, userId, {
        desktopNotifications: {
          ...desktopNotifications,
          desktopDevices: [oneSignalPlayerId, ...desktopDevices],
        },
      });
    }
  }, [
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  ]);

  const desktopNotificationModal = () => {
    if (!featureFlags?.desktopNotifications) {
      return modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['MY_ACCOUNT__ENABLE-DESKTOP-NOTIFICATIONS'],
        feature: 'desktopNotifications',
      });
    }

    return openDesktopNotificationsModal();
  };

  const tableLabels = [
    { label: 'Notification type' },
    {
      label: 'Push on Mobile',
      icon: <MobileOutlined className={styles.icon} />,
      popover: (
        <MobileDownloadPopover>
          <QuestionCircleOutlined />
        </MobileDownloadPopover>
      ),
    },
    {
      label: 'Push on Desktop',
      icon: <DesktopOutlined className={styles.icon} />,
      popover: (
        <PrimaryToolTip
          classNamePopoverContainer="notification-settings__tool-tip-body"
          anchorElement={
            <div className="notification-settings__tool-tip-icon">
              <QuestionCircleOutlined />
            </div>
          }
          position="top"
        >
          To get notifications you need to opt in on your desktop browser
        </PrimaryToolTip>
      ),
    },
    {
      label: 'Via Email',
      icon: <MailOutlined className={styles.icon} />,
      popover: (
        <PrimaryToolTip
          classNamePopoverContainer="notification-settings__tool-tip-body"
          anchorElement={
            <div className="notification-settings__tool-tip-icon">
              <QuestionCircleOutlined />
            </div>
          }
          position="top"
        >
          Receive notifications on your email address
        </PrimaryToolTip>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <SettingsSectionHeader
        title="Notification Settings"
        color="#DE5A3D"
        icon={<NotificationOutlined />}
      />
      {(!notificationServiceWorkerAdded || isUnsubscribedFromNotifications) && (
        <div className={styles.unsubscribedMessage}>
          Notifications are not enabled for this browser.
          <PrimaryButton
            className={styles.secondaryButton}
            color="transparent"
            onClick={desktopNotificationModal}
          >
            Enable Browser’s Notification
          </PrimaryButton>
        </div>
      )}

      {(areDesktopConversationsFollupsOn || areDesktopWidgetTriggeredOn) &&
        browserNotificationPermissionsStatus &&
        browserNotificationPermissionsStatus !== 'granted' && (
          <div className={styles.notificationDeniedWarning}>
            <ExclamationCircleOutlined className={styles.exclamationIcon} />
            <div className={styles.warningHeader}>
              <div style={{ fontWeight: 500 }}>
                You have blocked web push notification permissions for this
                browser.
              </div>
              <div>
                Check out{' '}
                <a
                  href="https://customers.ai/help/article/xjrvxif08a-how-to-unblock-notifications-permissions-on-your-browser"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  Help Docs
                </a>{' '}
                for instructions on unblocking notification permissions.
              </div>
            </div>
          </div>
        )}

      <div className={styles.table}>
        <div className={styles.tableRow}>
          {tableLabels.map(item => (
            <div className={styles.gridLabel} key={item.label}>
              {item?.icon}
              {item.label}
              {item?.popover}
            </div>
          ))}
        </div>
        <div className={styles.tableRow}>
          <div>Customer message</div>
          <NotificationSettings
            notificationType="allMessages"
            deviceType="pushNotifications"
          />
          <NotificationSettings
            notificationType="allMessages"
            deviceType="desktopNotifications"
            notificationChangeCallback={openDesktopNotificationsModal}
            notificationPageChangeCallback={openDesktopNotificationsModal}
          />
        </div>
        <div className={styles.tableRow}>
          <div>X-Ray Pixel lead is detected</div>
          <NotificationSettings
            notificationType="unicornLead"
            deviceType="pushNotifications"
          />
          <NotificationSettings
            notificationType="unicornLead"
            deviceType="desktopNotifications"
            notificationChangeCallback={openDesktopNotificationsModal}
            notificationPageChangeCallback={openDesktopNotificationsModal}
          />
          <NotificationSettings
            notificationType="unicornLead"
            deviceType="emailNotifications"
          />
        </div>

        <div className={styles.tableRow}>
          <div>Notification widget is triggered</div>
          <NotificationSettings
            notificationType="widgetTriggered"
            deviceType="pushNotifications"
          />
          <NotificationSettings
            notificationType="widgetTriggered"
            deviceType="desktopNotifications"
            notificationChangeCallback={openDesktopNotificationsModal}
            notificationPageChangeCallback={openDesktopNotificationsModal}
          />
        </div>

        <div className={styles.tableRow}>
          <div>Conversation requires follow up</div>
          <NotificationSettings
            notificationType="needsFollowUp"
            deviceType="pushNotifications"
          />
          <NotificationSettings
            notificationType="needsFollowUp"
            deviceType="desktopNotifications"
            notificationChangeCallback={openDesktopNotificationsModal}
            notificationPageChangeCallback={openDesktopNotificationsModal}
          />
        </div>

        <div className={styles.tableRow}>
          <div>Conversation was assigned to me</div>
          <NotificationSettings
            notificationType="assignedToMe"
            deviceType="pushNotifications"
          />
          <NotificationSettings
            notificationType="assignedToMe"
            deviceType="desktopNotifications"
            notificationChangeCallback={openDesktopNotificationsModal}
            notificationPageChangeCallback={openDesktopNotificationsModal}
          />
        </div>
      </div>
    </div>
  );
};
